import Link from "next/link";

import { routes } from "src/shared/routes";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";
import { Box } from "@mui/material";

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question: "How do I claim my tickets?",
    answer:
      "You will receive a text message at 9:00 AM on game day to confirm your attendance. Simply login and select the Attend button to guarantee a seat.",
  },
  {
    question: "Do I get a ticket to each game on the schedule?",
    answer:
      "Tickets are guaranteed for the predetermined games in the subscription. There are no penalties for not attending a game, however you need to confirm your attendance in order to receive a ticket. You can review your schedule on your homepage.",
  },
  {
    question: "Where are my tickets?",
    answer:
      "You will be notified tickets are available 3 hours prior to game start and are delivered within your Salt Lake Bees Account Manager.",
  },
  {
    question: "How are tickets sent to me?",
    answer:
      "3 hours prior to game start you will receive a text confirming that tickets have been delivered to your Salt Lake Bees Account Manager.",
  },
  {
    question: "Where will my seats be located?",
    answer: "Seat locations will vary by game.",
  },
  {
    question: "Can these tickets be transferred to a friend?",
    answer:
      "Summer Pass tickets, as well as seat upgrades and guest passes, are not eligible for resale or transfer. Abusing these guidelines can result in your Summer Pass being revoked.",
  },
  {
    question: "Can my seats be changed?",
    answer: (
      <>
        Once tickets have been sent to your account, they cannot be changed.
        Please contact{" "}
        <Link href="mailto:tickets@slbees.com">tickets@slbees.com</Link> for any
        assistance you may require.
      </>
    ),
  },
  {
    question: "Can I link tickets with other pass holders?",
    answer: (
      <>
        You can absolutely create a group to link with other pass holders!
        Navigate to the{" "}
        <Link
          href={{
            pathname: routes.my_group,
            query: { teamSchema: "saltlakebees_pass" },
          }}
        >
          My Group
        </Link>{" "}
        tab in your menu and copy the invite. This will ensure you are seated
        next to or close enough to hand a drink to one another for the game. You
        will need to ensure your group is created and all members have joined at
        least 4 hours prior to game start in order to be seated with one
        another.
      </>
    ),
  },
  {
    question: "Can I bring a friend to a game?",
    answer:
      "Guest tickets & upgrades will be available for select games. If available, guest tickets are available for purchase when you opt into games.",
  },
  {
    question: "Can I receive a refund if I cannot attend a game?",
    answer: "We do not issue any refunds for games you are not able to attend.",
  },
  {
    question: "How can I get a refund and cancel my pass?",
    answer: "An active purchase for the month is not refundable.",
  },
  {
    question:
      "How can I change my payment method or update my expiration date?",
    answer: (
      <>
        To update your payment method, simply navigate to the{" "}
        <Link
          href={{
            pathname: routes.manage_subscription,
            query: { teamSchema: "saltlakebees_pass" },
          }}
        >
          Manage Subscription
        </Link>{" "}
        menu on the right side of your screen. You will see a section to update
        your payment method where you can update new information.
      </>
    ),
  },
];

export const TERMS_AND_CONDITION: TTeamConfig["LANGUAGES"]["TERMS_AND_CONDITION"] =
  () => (
    <>
      By purchasing I agree to receive SMS alerts regarding ticket delivery and
      agree to the{" "}
      <Link href={"https://www.attend.tech/terms-of-use"} target={"_blank"}>
        Terms of Use
      </Link>{" "}
      and{" "}
      <Link href={"https://www.attend.tech/privacy-policy"} target={"_blank"}>
        Privacy Policy
      </Link>
      .
    </>
  );

export const TERMS_AND_CONDITION_OPT_OUT_RESTRICTED: TTeamConfig["LANGUAGES"]["TERMS_AND_CONDITION_OPT_OUT_RESTRICTED"] =
  () => (
    <>
      By purchasing you agree to receive SMS alerts regarding ticket delivery
      and agree to the{" "}
      <Link
        href={
          "https://cdn.prod.website-files.com/66b3d5184e88b53f24e71360/6836259e2aac5657874b9955_Attend%20Terms%20of%20Use%20052425.pdf"
        }
        target={"_blank"}
      >
        Terms of Use
      </Link>{" "}
      and{" "}
      <Link
        href={
          "https://cdn.prod.website-files.com/66b3d5184e88b53f24e71360/683625a812daf6ceadcbe532_Attend%20Privacy%20Policy%20052725.pdf"
        }
        target={"_blank"}
      >
        Privacy Policy
      </Link>
      .
    </>
  );

export const PASS_PURCHASE_SUCCESS_MESSAGE: TTeamConfig["LANGUAGES"]["PASS_PURCHASE_SUCCESS_MESSAGE"] =
  {
    TITLE: () => (
      <>
        You&apos;e all set to experience the action! Don&apos;t forget to
        confirm attendance here on game day!
      </>
    ),
    DESCRIPTION: () => {
      const router = useRouterExt();
      return (
        <>
          Friends don&apos;t leave friends behind.{" "}
          <b>
            <u>
              <span
                onClick={() => router.pushSchema(routes.my_group)}
                style={{ cursor: "pointer" }}
              >
                Invite your friends
              </span>
            </u>
          </b>{" "}
          and enjoy games together.
        </>
      );
    },
  };

export const HomePageZeroStateDesc = () => {
  const router = useRouterExt();
  const teamSchema = router?.query?.teamSchema as string;

  return (
    <Box component={"span"}>
      You can purchase one{" "}
      <Link
        className="global-link"
        href={{
          pathname: routes.landing,
          query: { teamSchema },
        }}
      >
        here
      </Link>
      , or check your{" "}
      <Link
        className="global-link"
        href={{
          pathname: routes.purchase_history,
          query: { teamSchema },
        }}
      >
        Purchase History
      </Link>{" "}
      to see passes you’ve gifted.
    </Box>
  );
};

export const RedeemGiftSuccessModalDesc = () => {
  const router = useRouterExt();
  const teamSchema = router?.query?.teamSchema as string;

  return (
    <Box component={"span"}>
      Your gift is all set! Create a{" "}
      <Link
        className="global-link"
        href={{
          pathname: routes.my_group,
          query: { teamSchema },
        }}
      >
        group
      </Link>{" "}
      to bring friends along, or explore the schedule in{" "}
      <Link
        className="global-link"
        href={{
          pathname: routes.home,
          query: { teamSchema },
        }}
      >
        Upcoming Games
      </Link>
      . We’ll send you an SMS when check-in opens as the season gets closer.
    </Box>
  );
};
